/**
 * Sign-in from inside an iframe (a Microsoft Teams tab, or another host).
 *
 * Entra refuses to render its login page inside a frame, so an embedded tab
 * cannot simply navigate to `/api/auth/sso/login`.
 *
 * Inside Teams the tab first signs in silently with the Teams SSO token
 * (`signInSilentlyInTeams`); only when that fails does it offer the button.
 * Its sign-in window must be opened by the Teams SDK
 * (`authentication.authenticate`): the desktop and mobile clients block a
 * plain `window.open` from a tab. That window may also keep its cookies apart
 * from the tab's, so it does not hand over a cookie. The backend ends the
 * sign-in on `/teams-auth-end` with a one-minute, single-use code, that page
 * passes it back with `notifySuccess`, and the tab redeems it at
 * `/api/auth/teams/redeem` for its own session cookie.
 *
 * Any other host gets a plain popup: the backend sets the session cookie on
 * the callback, the popup lands on `/home`, sees its own name and closes (see
 * `AppShell`), and the tab notices the new session by polling `/api/auth/me`.
 */

import { app, authentication } from "@microsoft/teams-js";

export const LOGIN_POPUP_NAME = "norican-card-login";

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 3 * 60 * 1000;
const TEAMS_INIT_TIMEOUT_MS = 3000;
const POPUP_WIDTH = 520;
const POPUP_HEIGHT = 640;

/** True when the app runs embedded (Teams, SharePoint web part, ...). */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true; // cross-origin parent — definitely framed
  }
}

/**
 * True when the page may be running inside a Teams client: framed (desktop
 * and web), or the Teams mobile webview, which loads a tab as a top-level
 * page (no frame) and identifies itself in the user agent
 * (`TeamsMobile-iOS` / `TeamsMobile-Android`) and with the
 * `nativeInterface` bridge it injects. Only a hint; `inTeams` confirms it.
 */
export function mayBeTeams(): boolean {
  return isEmbedded() || /\bTeamsMobile-/.test(navigator.userAgent) || "nativeInterface" in window;
}

let teamsHost: Promise<boolean> | null = null;

/**
 * True when the page runs inside a Teams client (tab or the SDK's
 * authentication window). Initialises the SDK once; a page outside Teams
 * never answers the handshake, so this gives up after a few seconds.
 */
export function inTeams(): Promise<boolean> {
  teamsHost ??= Promise.race([
    app.initialize().then(
      () => true,
      () => false,
    ),
    new Promise<boolean>((resolve) =>
      window.setTimeout(() => resolve(false), TEAMS_INIT_TIMEOUT_MS),
    ),
  ]);
  return teamsHost;
}

/**
 * Sign in without any prompt: Teams issues this app's SSO token to the tab
 * (`getAuthToken`, scope `access_as_user`, pre-authorised on the app
 * registration) and the backend exchanges it for the session cookie. Rejects
 * when Teams cannot issue the token silently; the caller then offers the
 * interactive sign-in.
 */
export async function signInSilentlyInTeams(): Promise<void> {
  const token = await authentication.getAuthToken();
  const response = await fetch("/api/auth/teams/sso", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) {
    throw new Error(`teams_sso_failed_${response.status}`);
  }
}

/**
 * Sign in through the Teams SDK's authentication window and redeem the
 * hand-off code it returns for this tab's session cookie.
 */
export async function signInViaTeams(loginUrl: string): Promise<void> {
  const code = await authentication.authenticate({
    url: new URL(`${loginUrl}?teams=1`, window.location.origin).href,
    width: POPUP_WIDTH,
    height: POPUP_HEIGHT,
  });
  const response = await fetch("/api/auth/teams/redeem", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!response.ok) {
    throw new Error(`redeem_failed_${response.status}`);
  }
}

/**
 * The end of a Teams sign-in, inside the SDK's authentication window: pass
 * the hand-off code from the URL fragment back to the tab.
 */
export async function finishTeamsSignIn(): Promise<void> {
  await app.initialize();
  const code = new URLSearchParams(window.location.hash.slice(1)).get("handoff");
  if (code) {
    authentication.notifySuccess(code);
  } else {
    authentication.notifyFailure("no_handoff");
  }
}

/**
 * Open the SSO login in a popup and resolve once `/api/auth/me` answers 200
 * (or the user closed the popup / the wait timed out, which rejects).
 */
export function signInViaPopup(loginUrl: string): Promise<void> {
  const left = Math.max(0, (window.screen.width - POPUP_WIDTH) / 2);
  const top = Math.max(0, (window.screen.height - POPUP_HEIGHT) / 2);
  const popup = window.open(
    loginUrl,
    LOGIN_POPUP_NAME,
    `popup=yes,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`,
  );
  if (!popup) {
    return Promise.reject(new Error("popup_blocked"));
  }
  const started = Date.now();
  return new Promise<void>((resolve, reject) => {
    const timer = window.setInterval(async () => {
      if (Date.now() - started > POLL_TIMEOUT_MS) {
        window.clearInterval(timer);
        reject(new Error("timeout"));
        return;
      }
      try {
        const response = await fetch("/api/auth/me", { credentials: "same-origin" });
        if (response.ok) {
          window.clearInterval(timer);
          if (!popup.closed) {
            popup.close();
          }
          resolve();
          return;
        }
      } catch {
        // backend hiccup — keep polling until the timeout
      }
      if (popup.closed) {
        window.clearInterval(timer);
        reject(new Error("closed"));
      }
    }, POLL_INTERVAL_MS);
  });
}
